/**
 * Live shift values against the organization's KPI targets, the shift clock,
 * the output outlook and the insights of the Live Shift tabs (machine and
 * line). Pure functions: the rules are unit-tested, the components only draw.
 * How the values are calculated is in the help ("Live shift KPIs").
 */

import type {
  LiveShiftData,
  LiveStatusTransition,
  TopStatus,
  TopStatusData,
} from "../../hooks/useLiveShiftData";
import {
  resolveAll,
  type Kpi,
  type KpiScope,
  type KpiTargetRow,
  type TargetSubject,
} from "../../services/KpiTargetService";
import { isMachineRunning } from "../../utils/machineStatus";
import type { CauseKey, LineFlow } from "./lineFlow";

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** Before this share of the shift has passed, no pace and no projection. */
export const WARM_UP_FRACTION = 0.15;
/** A value this close to its target (10 %) is "close", not "missed". */
export const CLOSE_TOLERANCE = 0.1;
/** Live messages older than this are shown as stale. */
export const STALE_AFTER_MS = 15 * MINUTE_MS;

// ---------------------------------------------------------------- targets

export type TargetSource = KpiScope | "live";

export interface LiveTarget {
  value: number;
  source: TargetSource;
}

export type LiveTargets = Partial<Record<Kpi, LiveTarget>>;

/**
 * The targets of a machine: the organization's (machine, else line, else
 * machine type; output skips the line) override the live data's; the live
 * output target is used only when the organization has none.
 */
export function liveTargets(
  index: Map<string, KpiTargetRow>,
  subject: TargetSubject,
  liveOutputTarget: number | null | undefined,
): LiveTargets {
  const out: LiveTargets = {};
  for (const [kpi, r] of Object.entries(resolveAll(index, subject)) as [Kpi, { value: number; scope: KpiScope }][]) {
    out[kpi] = { value: r.value, source: r.scope };
  }
  if (!out.output && typeof liveOutputTarget === "number" && liveOutputTarget > 0) {
    out.output = { value: liveOutputTarget, source: "live" };
  }
  return out;
}

// ------------------------------------------------------------------ values

/** A machine's live values in the units of the targets. */
export interface LiveValues {
  /** Good products this shift. */
  output: number | null;
  waste: number | null;
  /** Percent. */
  efficiency: number | null;
  utilization: number | null;
  rejectRatio: number | null;
  /** Non-running time this shift (the stops of the top-status list). */
  downtimeSeconds: number;
  mtbfMinutes: number | null;
  speed: number | null;
  nominalSpeed: number | null;
}

/** Stops of the top-status list: every status in which the machine did not run. */
export const stopsOf = (topStops: TopStatusData | null | undefined): TopStatus[] =>
  (topStops?.items ?? []).filter((s) => !isMachineRunning(s.name, s.code));

export function liveValues(
  shift: LiveShiftData | null | undefined,
  topStops: TopStatusData | null | undefined,
): LiveValues {
  const k = shift?.kpis;
  const output = k?.good_products ?? null;
  const waste = k?.waste ?? null;
  const pct = (v: number | null | undefined) => (typeof v === "number" ? v * 100 : null);
  return {
    output,
    waste,
    efficiency: pct(k?.machine_efficiency),
    utilization: pct(k?.utilization_ratio),
    rejectRatio:
      output !== null && waste !== null && output + waste > 0 ? (waste / (output + waste)) * 100 : null,
    downtimeSeconds: stopsOf(topStops).reduce((sum, s) => sum + (s.duration_seconds || 0), 0),
    mtbfMinutes: k?.mtbf_seconds ? k.mtbf_seconds / 60 : null,
    speed: k?.current_speed ?? null,
    nominalSpeed: k?.nominal_speed ?? null,
  };
}

// ------------------------------------------------------------------- clock

export interface ShiftClock {
  durationMs: number;
  elapsedMs: number;
  remainingMs: number;
  /** Elapsed share of the shift, 0 to 1. */
  fraction: number;
}

/**
 * Elapsed and remaining time of a shift. The edge writes the shift's local
 * wall-clock times into UTC fields, so "now" is the local wall clock in the
 * same frame (as the Live Shift progress bar always did); overnight shifts
 * end the next day.
 */
export function shiftClock(start: string, end: string, now: Date = new Date()): ShiftClock | null {
  const startMs = new Date(start).getTime();
  let endMs = new Date(end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  if (endMs <= startMs) endMs += DAY_MS;
  const nowMs = Date.UTC(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
  );
  const durationMs = endMs - startMs;
  const elapsedMs = Math.min(Math.max(nowMs - startMs, 0), durationMs);
  return {
    durationMs,
    elapsedMs,
    remainingMs: durationMs - elapsedMs,
    fraction: durationMs > 0 ? elapsedMs / durationMs : 0,
  };
}

/** The live message is recent enough to count as live. */
export const isFresh = (timestamp: string | undefined, nowMs: number = Date.now()): boolean => {
  const t = timestamp ? new Date(timestamp).getTime() : NaN;
  return Number.isFinite(t) && nowMs - t <= STALE_AFTER_MS;
};

// ------------------------------------------------------------------ status

export type Status = "ok" | "warn" | "bad";

/** Met, close (within 10 %) or missed, for a minimum or a maximum target. */
export function statusOf(value: number, target: number, kind: "min" | "max"): Status {
  if (kind === "min") {
    if (value >= target) return "ok";
    return value >= target * (1 - CLOSE_TOLERANCE) ? "warn" : "bad";
  }
  if (value <= target) return "ok";
  return value <= target * (1 + CLOSE_TOLERANCE) ? "warn" : "bad";
}

/**
 * Downtime against the shift's maximum: missed once over the maximum, close
 * once over its share of the elapsed shift (at least the warm-up share).
 */
export function downtimeStatus(usedSeconds: number, maxSeconds: number, fraction: number): Status {
  if (usedSeconds > maxSeconds) return "bad";
  return usedSeconds > maxSeconds * Math.max(fraction, WARM_UP_FRACTION) ? "warn" : "ok";
}

// ----------------------------------------------------------------- outlook

export interface OutputOutlook {
  output: number;
  target: number;
  /** output / target · 100 */
  completionPct: number;
  /** target · elapsed share */
  expected: number;
  /** output − expected */
  gap: number;
  /** completion / elapsed share; null while warming up */
  pacePct: number | null;
  /** output / elapsed share; null while warming up */
  projected: number | null;
  /** (target − output) / remaining minutes; null once reached or at the end */
  neededPerMin: number | null;
  /** output / elapsed minutes */
  avgPerMin: number | null;
  remainingMs: number;
  reached: boolean;
}

export function outputOutlook(
  output: number | null,
  target: number | null | undefined,
  clock: ShiftClock | null,
): OutputOutlook | null {
  if (output === null || !target || target <= 0 || !clock) return null;
  const f = clock.fraction;
  const warming = f < WARM_UP_FRACTION;
  const elapsedMin = clock.elapsedMs / MINUTE_MS;
  const remainingMin = clock.remainingMs / MINUTE_MS;
  return {
    output,
    target,
    completionPct: (output / target) * 100,
    expected: target * f,
    gap: output - target * f,
    pacePct: warming ? null : (output / target / f) * 100,
    projected: warming ? null : output / f,
    neededPerMin: output < target && remainingMin >= 1 ? (target - output) / remainingMin : null,
    avgPerMin: elapsedMin >= 1 ? output / elapsedMin : null,
    remainingMs: clock.remainingMs,
    reached: output >= target,
  };
}

// ---------------------------------------------------------------- insights

export interface MachineRef {
  label: string;
  sn?: string;
}

export type Insight =
  | { kind: "outputReached"; severity: "ok"; output: number; target: number }
  | { kind: "outputAhead"; severity: Status; gap: number; projected: number | null; target: number }
  | { kind: "outputBehind"; severity: Status; gap: number; projected: number | null; target: number }
  | { kind: "outputNeeded"; severity: "info"; neededPerMin: number; avgPerMin: number; remainingMs: number }
  | { kind: "belowTarget"; severity: Status; kpi: Kpi; value: number; target: number; machine?: MachineRef }
  | { kind: "aboveMax"; severity: Status; kpi: Kpi; value: number; target: number; machine?: MachineRef }
  | { kind: "downtimeBudget"; severity: Status; usedSeconds: number; maxSeconds: number; machine?: MachineRef }
  | { kind: "topStop"; severity: "info"; name: string; seconds: number; count: number; sharePct: number; machine?: MachineRef }
  | { kind: "lineDownNow"; severity: "bad"; cause: MachineRef; since: number; reason?: string }
  | {
      kind: "topBlocker";
      severity: "warn";
      cause: MachineRef;
      seconds: number;
      sharePct: number;
      waitingSeconds: number;
      reason?: string;
    };

export type InsightSeverity = Insight["severity"];

const RANK: Record<InsightSeverity, number> = { bad: 0, warn: 1, info: 2, ok: 3 };

export const sortInsights = (list: Insight[]): Insight[] =>
  list
    .map((insight, i) => ({ insight, i }))
    .sort((a, b) => RANK[a.insight.severity] - RANK[b.insight.severity] || a.i - b.i)
    .map((x) => x.insight);

/** Output on track, ahead, behind, reached, and the rate it still needs. */
export function outputInsights(outlook: OutputOutlook | null): Insight[] {
  if (!outlook) return [];
  if (outlook.reached) {
    return [{ kind: "outputReached", severity: "ok", output: outlook.output, target: outlook.target }];
  }
  if (outlook.pacePct === null) return [];
  const list: Insight[] = [
    {
      kind: outlook.gap >= 0 ? "outputAhead" : "outputBehind",
      severity: statusOf(outlook.pacePct, 100, "min"),
      gap: Math.abs(outlook.gap),
      projected: outlook.projected,
      target: outlook.target,
    },
  ];
  if (
    outlook.neededPerMin !== null &&
    outlook.avgPerMin !== null &&
    outlook.neededPerMin > outlook.avgPerMin
  ) {
    list.push({
      kind: "outputNeeded",
      severity: "info",
      neededPerMin: outlook.neededPerMin,
      avgPerMin: outlook.avgPerMin,
      remainingMs: outlook.remainingMs,
    });
  }
  return list;
}

/** Efficiency, utilization, MTBF, reject ratio and downtime against their targets. */
export function targetInsights(
  values: LiveValues,
  targets: LiveTargets,
  clock: ShiftClock | null,
  machine?: MachineRef,
): Insight[] {
  const list: Insight[] = [];
  const minimum = (kpi: Kpi, value: number | null) => {
    const target = targets[kpi];
    if (value === null || !target) return;
    const severity = statusOf(value, target.value, "min");
    if (severity !== "ok") list.push({ kind: "belowTarget", severity, kpi, value, target: target.value, machine });
  };
  minimum("efficiency", values.efficiency);
  minimum("utilization", values.utilization);
  minimum("minMTBF", values.mtbfMinutes);

  const reject = targets.rejectRatio;
  if (values.rejectRatio !== null && reject) {
    const severity = statusOf(values.rejectRatio, reject.value, "max");
    if (severity !== "ok") {
      list.push({ kind: "aboveMax", severity, kpi: "rejectRatio", value: values.rejectRatio, target: reject.value, machine });
    }
  }

  const max = targets.maxDowntime;
  if (max && clock) {
    const maxSeconds = max.value * 3600;
    const severity = downtimeStatus(values.downtimeSeconds, maxSeconds, clock.fraction);
    if (severity !== "ok") {
      list.push({ kind: "downtimeBudget", severity, usedSeconds: values.downtimeSeconds, maxSeconds, machine });
    }
  }
  return list;
}

/** The stop that cost the most time this shift, and its share of the downtime. */
export function topStopInsight(
  topStops: TopStatusData | null | undefined,
  machine?: MachineRef,
): Extract<Insight, { kind: "topStop" }> | null {
  const stops = stopsOf(topStops).filter((s) => s.duration_seconds > 0);
  const total = stops.reduce((sum, s) => sum + s.duration_seconds, 0);
  const top = [...stops].sort((a, b) => b.duration_seconds - a.duration_seconds)[0];
  if (!top || total <= 0) return null;
  return {
    kind: "topStop",
    severity: "info",
    name: top.name || `Status ${top.code}`,
    seconds: top.duration_seconds,
    count: top.quantity,
    sharePct: (top.duration_seconds / total) * 100,
    machine,
  };
}

/** The insights of a machine's Live Shift tab. */
export function machineInsights(
  values: LiveValues,
  targets: LiveTargets,
  outlook: OutputOutlook | null,
  clock: ShiftClock | null,
  topStops: TopStatusData | null | undefined,
): Insight[] {
  const top = topStopInsight(topStops);
  return sortInsights([
    ...outputInsights(outlook),
    ...targetInsights(values, targets, clock),
    ...(top ? [top] : []),
  ]);
}

// -------------------------------------------------------------------- line

/** A machine of a line with its live values, for the line's Live Shift tab. */
export interface StationLiveState {
  machine: MachineRef;
  values: LiveValues;
  targets: LiveTargets;
  /** Its live messages are recent (see STALE_AFTER_MS). */
  fresh: boolean;
}

/** Running now, from the last status transition; null without one. */
export const runningNow = (transition: LiveStatusTransition | undefined): boolean | null =>
  transition ? isMachineRunning(transition.transition.to_name, transition.transition.to) : null;

/** At most this many machines below their efficiency targets are listed. */
const MAX_MACHINES_BELOW = 2;

/**
 * The insights of a line's Live Shift tab: why the line is down now, which
 * machine stopped it most this shift, the line output against its target,
 * and the machines furthest below their efficiency targets.
 */
export function lineInsights(
  flow: LineFlow | null,
  causeRef: (key: CauseKey) => MachineRef,
  lineOutlook: OutputOutlook | null,
  machines: StationLiveState[],
): Insight[] {
  const list: Insight[] = [];

  if (flow?.current.down && flow.current.cause) {
    list.push({
      kind: "lineDownNow",
      severity: "bad",
      cause: causeRef(flow.current.cause),
      since: flow.current.since ?? flow.nowMs,
      reason: flow.current.reason,
    });
  }

  const top = flow?.causes[0];
  if (flow && top && top.lineDownSeconds > 0 && flow.lineDownSeconds > 0) {
    list.push({
      kind: "topBlocker",
      severity: "warn",
      cause: causeRef(top.key),
      seconds: top.lineDownSeconds,
      sharePct: (top.lineDownSeconds / flow.lineDownSeconds) * 100,
      waitingSeconds: top.waitingCausedSeconds,
      reason: top.reasons[0]?.text,
    });
  }

  list.push(...outputInsights(lineOutlook));

  // furthest below its efficiency target first (relative gap)
  const below = machines
    .filter((m) => m.fresh)
    .flatMap((m) =>
      targetInsights(m.values, m.targets, null, m.machine).filter(
        (i): i is Extract<Insight, { kind: "belowTarget" }> => i.kind === "belowTarget" && i.kpi === "efficiency",
      ),
    )
    .sort((a, b) => a.value / a.target - b.value / b.target)
    .slice(0, MAX_MACHINES_BELOW);
  list.push(...below);

  return sortInsights(list);
}

/** Output not made while the line stood: its down time at the output machines' nominal speed. */
export function lostOutput(
  lineDownSeconds: number,
  outputs: { sn: string; nominalSpeed: number | null }[],
  contains: Record<string, number>,
): number | null {
  const speeds = outputs.filter((o) => (o.nominalSpeed ?? 0) > 0);
  if (lineDownSeconds <= 0 || speeds.length === 0) return lineDownSeconds <= 0 ? 0 : null;
  const perMinute = speeds.reduce(
    (sum, o) => sum + (o.nominalSpeed as number) * (contains[o.sn] > 0 ? contains[o.sn] : 1),
    0,
  );
  return (lineDownSeconds / 60) * perMinute;
}

/**
 * The line output now: the output machines' good products times their
 * "Contains". Its target: the line's own output target, else the output
 * machines' targets (`target`, as resolved for each machine) times their
 * "Contains".
 */
export function lineLiveOutput(
  outputs: { sn: string; shift?: LiveShiftData; fresh: boolean; target?: LiveTarget }[],
  contains: Record<string, number>,
  lineOutputTarget: number | undefined,
): { output: number | null; target: LiveTarget | null } {
  const factor = (sn: string) => (contains[sn] > 0 ? contains[sn] : 1);
  const counted = outputs.filter((o) => o.fresh && typeof o.shift?.kpis.good_products === "number");
  const output = counted.length
    ? counted.reduce((sum, o) => sum + (o.shift!.kpis.good_products as number) * factor(o.sn), 0)
    : null;

  let target: LiveTarget | null = null;
  if (typeof lineOutputTarget === "number" && lineOutputTarget > 0) {
    target = { value: lineOutputTarget, source: "line" };
  } else if (counted.length && counted.every((o) => (o.target?.value ?? 0) > 0)) {
    target = {
      value: counted.reduce((sum, o) => sum + o.target!.value * factor(o.sn), 0),
      source: counted.every((o) => o.target!.source === "live") ? "live" : counted[0].target!.source,
    };
  }
  return { output, target };
}

// ------------------------------------------------------------------ format

/** Seconds as H:MM:SS. */
export function formatHms(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** Local clock time of an instant, HH:MM. */
export function localTime(iso: string | undefined): string {
  const d = iso ? new Date(iso) : null;
  return d && Number.isFinite(d.getTime())
    ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    : "--";
}

/** Milliseconds as H:MM. */
export function formatHm(ms: number): string {
  const totalMin = Math.max(0, Math.round(ms / MINUTE_MS));
  return `${Math.floor(totalMin / 60)}:${String(totalMin % 60).padStart(2, "0")}`;
}
